import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import { AD_MANAGEMENT_API } from 'configs/api/adManagement';
import { ID_COLUMN_WIDTH } from 'configs/common';
import { AD_BID_STATUS_FILTERS_CONFIG } from '../configs';
import {
	LocalTimeDate,
	THttpRequestConfig,
	useAuthenticatedInformation,
	useForceRefreshData,
	useHttpClient,
} from 'hooks';
import { IAdSectionBid } from 'types/adManagement';
import { EAdBidListStatusActions, IAdBidListTableData } from './types';
import ActionsMenu, { IActionMenuItem, getActionButtonsColumnWidth } from 'components/ActionsMenu';
import { CurrencySuffix } from 'components/CurrencySuffix';
import PageLayout from 'components/PageLayout';
import { SchemaComponent } from 'components/SchemaComponent/SchemaComponent';
import TableWrapper from 'components/Table';
import AdBidStatusTag from 'components/_tags/AdBidStatusTag';
import { App, Button, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PauseCircleOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';

export const AdBidList: FC = () => {
	const { t: tCommon } = useTranslation('common');
	const { t: tAdManagement } = useTranslation('ad-management');

	const { vendorId: vendorIdParam } = useParams();
	const vendorId = +vendorIdParam!;

	const { message } = App.useApp();
	const { user } = useAuthenticatedInformation();
	const { forceRefreshData, refreshingData } = useForceRefreshData();

	const selectedVendor = user.vendorMap[vendorId];

	// ! http clients
	const updateStatusHttpClient = useHttpClient();

	// ! handlers
	const handleChangeAdBidStatus = (action: EAdBidListStatusActions, addBidId: number) => {
		const config: Record<EAdBidListStatusActions, THttpRequestConfig> = {
			[EAdBidListStatusActions.RESUME]: AD_MANAGEMENT_API.AD_BIDS.resume(vendorId, addBidId),
			[EAdBidListStatusActions.PAUSE]: AD_MANAGEMENT_API.AD_BIDS.pause(vendorId, addBidId),
			[EAdBidListStatusActions.CANCEL]: AD_MANAGEMENT_API.AD_BIDS.cancel(vendorId, addBidId),
		};

		updateStatusHttpClient.request({
			requestConfig: config[action],
			successCallback: () => {
				message.success(tAdManagement('actions.status_success_message', { id: addBidId }), 5);
				refreshingData();
			},
		});
	};

	// ! memos
	const requestConfig = useMemo(() => AD_MANAGEMENT_API.AD_BIDS.list(vendorId), [vendorId]);

	const columns = useMemo<ColumnsType<IAdBidListTableData>>(
		() => [
			{
				key: 'id',
				title: tCommon('table.headers.id'),
				dataIndex: 'id',
				width: ID_COLUMN_WIDTH,
			},
			{
				key: 'name',
				title: tCommon('table.headers.name'),
				render: (_, record) => record.ad_section.name,
				width: 240,
			},
			{
				key: 'ad_section',
				title: tAdManagement('table.headers.ad_section'),
				dataIndex: 'ad_section',
				render: (_, { ad_section }) => (
					<Flex
						gap='middle'
						justify='center'
						style={{ minWidth: '100%', maxWidth: 300 }}
					>
						<span>#{ad_section.id}</span>
						{ad_section.app_component && (
							<SchemaComponent
								type={ad_section.app_component.type}
								content={{
									id: ad_section.app_component.id,
									type: ad_section.app_component.type,
									component: ad_section.app_component,
								}}
							/>
						)}
					</Flex>
				),
				width: 340,
			},
			{
				key: 'start_date',
				title: tAdManagement('table.headers.start_date'),
				render: (_, { start_date }) => <LocalTimeDate date={start_date} />,
				sorter: true,
				width: 120,
			},
			{
				key: 'budget_total',
				title: tAdManagement('table.headers.budget_total'),
				render: (_, { budget }) => <CurrencySuffix>{budget.total}</CurrencySuffix>,
				sorter: true,
				width: 120,
			},
			{
				key: 'budget_spent',
				title: tAdManagement('table.headers.budget_spent'),
				render: (_, { budget }) => <CurrencySuffix>{budget.spent}</CurrencySuffix>,
				sorter: true,
				width: 120,
			},
			{
				key: 'price_per_click',
				title: tAdManagement('table.headers.price_per_click'),
				render: (_, { price_per_click }) => <CurrencySuffix>{price_per_click}</CurrencySuffix>,
				sorter: true,
				defaultSortOrder: 'descend',
				width: 120,
			},
			{
				key: 'status',
				title: tAdManagement('table.headers.status'),
				width: 140,
				render: (_, { status }) => <AdBidStatusTag status={status} />,
				filters: AD_BID_STATUS_FILTERS_CONFIG,
			},
			// * Actions
			{
				key: 'actions',
				title: tCommon('table.headers.actions'),
				width: getActionButtonsColumnWidth(4),
				fixed: 'right',
				render: (_, { id, actions }) => {
					const actionList: IActionMenuItem[] = [
						{
							type: 'button',
							title: tCommon('action_buttons.resume'),
							icon: <PlayCircleOutlined />,
							disabled: !actions.can_resume,
							actionCb: () => handleChangeAdBidStatus(EAdBidListStatusActions.RESUME, id),
						},
						{
							type: 'button',
							title: tCommon('action_buttons.pause'),
							icon: <PauseCircleOutlined />,
							disabled: !actions.can_pause,
							actionCb: () => handleChangeAdBidStatus(EAdBidListStatusActions.PAUSE, id),
						},
						{
							type: 'button',
							title: tCommon('action_buttons.cancel'),
							icon: <StopOutlined />,
							disabled: !actions.can_cancel,
							actionCb: () => handleChangeAdBidStatus(EAdBidListStatusActions.CANCEL, id),
						},
						{
							type: 'link',
							title: tCommon('action_buttons.view'),
							icon: <EyeOutlined />,
							disabled: !actions.can_edit,
							navigateTo: `bid/${id}`,
						},
					];

					return <ActionsMenu actions={actionList} />;
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	// ! render
	return (
		<PageLayout
			header={
				<PageHeader
					title={tAdManagement('page_titles.list')}
					subTitle={`[#${vendorId}] ${selectedVendor?.name}`}
					extra={
						<NavLink to={'new'}>
							<Button type='primary'>{tAdManagement('actions.add')}</Button>
						</NavLink>
					}
				/>
			}
		>
			<TableWrapper<IAdBidListTableData, IAdSectionBid>
				columns={columns}
				searchParam='app_component'
				searchPlaceholder={tAdManagement('table.search_placeholder')}
				requestConfig={requestConfig}
				refetchData={forceRefreshData}
				transformDataToTableData={(data: any) => ({ key: data.id, ...data })}
				scrollX={'max-content'}
			/>
		</PageLayout>
	);
};
